import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export function TeamSection() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

  const setRole = async (u, role) => {
    try {
      await api.put(`/users/${u.user_id}/role`, { role });
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(`${u.name} is now ${role === "admin" ? "an admin" : "a member"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update role");
    }
  };

  return (
    <section className="panel" data-testid="team-section">
      <div className="p-6 sm:p-8 border-b border-border">
        <h2 className="font-display text-xl font-bold">Team & access</h2>
        <p className="text-sm text-muted-foreground mt-2">Anyone who signs in with Google joins as a member (own documents only). Admins see every document and manage these settings. Share the app link to invite someone.</p>
      </div>
      <ul>
        {users.map((u) => (
          <li key={u.user_id} className="flex flex-wrap items-center gap-4 px-6 sm:px-8 py-4 border-b border-border/40 last:border-0" data-testid={`team-user-${u.user_id}`}>
            {u.picture ? <img src={u.picture} alt="" className="w-9 h-9 object-cover" /> : <div className="w-9 h-9 bg-muted grid place-items-center font-display font-black">{u.name?.[0]}</div>}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{u.name} {u.user_id === me.user_id && <span className="text-muted-foreground font-normal">(you)</span>}</div>
              <div className="text-xs text-muted-foreground font-mono truncate">{u.email}</div>
            </div>
            <span className={`text-[10px] tracking-[0.2em] uppercase font-semibold border px-2 py-0.5 ${u.role === "admin" ? "border-primary text-primary" : "border-border text-muted-foreground"}`} data-testid={`team-role-${u.user_id}`}>{u.is_owner ? "owner" : u.role}</span>
            {!u.is_owner && u.user_id !== me.user_id && (
              u.role === "admin" ? (
                <button onClick={() => setRole(u, "user")} data-testid={`team-demote-${u.user_id}`} className="h-9 px-3 text-xs font-semibold border border-border hover:border-foreground flex items-center gap-2"><ShieldOff className="w-4 h-4" strokeWidth={1.5} /> Remove admin</button>
              ) : (
                <button onClick={() => setRole(u, "admin")} data-testid={`team-promote-${u.user_id}`} className="btn-press h-9 px-3 text-xs font-semibold border border-foreground hover:bg-foreground hover:text-background flex items-center gap-2"><ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> Make admin</button>
              )
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
